if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        const wasActive = activeMedia.classList.contains('is-active');
        const wasFirst = activeMedia.parentElement.firstElementChild === activeMedia;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (!wasFirst) activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (!wasFirst && this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        const activeImage = activeMedia.querySelector('img');
        if (activeImage) {
          activeImage.loading = 'eager';
          activeImage.fetchPriority = 'high';
        }

        this.preventStickyHeader();

        // If the same featured media was already activated instantly by the
        // swatch interaction, the later Shopify variant response must not
        // trigger a second scroll.
        if (!(wasActive && (!prepend || wasFirst))) {
          requestAnimationFrame(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
            }

            const activeMediaRect = activeMedia.getBoundingClientRect();
            const header = document.querySelector('.section-header');
            const headerRect = header?.getBoundingClientRect();
            const headerBottom =
              headerRect && headerRect.bottom > 0 && headerRect.top <= 0
                ? headerRect.bottom
                : 0;
            const safeTop = Math.max(0, headerBottom) + 12;

            // Only move the page upward when the newly selected first image is
            // above the visible content area.
            if (activeMediaRect.top >= safeTop) return;

            const top = Math.max(0, window.scrollY + activeMediaRect.top - safeTop);
            window.scrollTo({ top, behavior: 'smooth' });
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/* CostumeGlamour: instant variant media preview.
   The normal Shopify variant request still updates price, inventory, URL, etc.
   This only makes swatch media react immediately and survives picker DOM refreshes. */
(() => {
  if (window.__cgFastVariantMediaBound) return;
  window.__cgFastVariantMediaBound = true;

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const picker = target.closest('variant-selects');
      const swatchGroup = target.closest('.product-form__input--swatch');
      if (!picker || !swatchGroup) return;

      const productInfo = picker.closest('product-info');
      const gallery = productInfo?.querySelector('media-gallery');
      const mapNode = picker.querySelector('[data-cg-variant-media-map]');
      if (!productInfo || !gallery || !mapNode) return;

      let variants = [];
      try {
        variants = JSON.parse(mapNode.textContent || '[]');
      } catch (error) {
        console.warn('Variant media map could not be parsed', error);
        return;
      }

      const groups = Array.from(
        picker.querySelectorAll('fieldset, .product-form__input--dropdown')
      );
      const changedGroup = target.closest('fieldset, .product-form__input--dropdown');
      const changedIndex = groups.indexOf(changedGroup);
      if (changedIndex < 0) return;

      const selected = groups.map((group) => {
        const radio = group.querySelector('input:checked');
        if (radio) return radio.value;
        return group.querySelector('select')?.value || null;
      });

      let match = variants.find(
        (variant) =>
          variant.mediaId &&
          variant.options?.length === selected.length &&
          variant.options.every(
            (value, index) => !selected[index] || value === selected[index]
          )
      );

      if (!match?.mediaId) {
        const changedValue = target.value;
        match = variants.find(
          (variant) =>
            variant.mediaId && variant.options?.[changedIndex] === changedValue
        );
      }

      if (!match?.mediaId) return;

      const mediaKey = `${productInfo.dataset.section}-${match.mediaId}`;
      const media = gallery.querySelector(`[data-media-id="${mediaKey}"]`);
      const image = media?.querySelector('img');
      if (image) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
      }

      gallery.setActiveMedia?.(mediaKey, true);
    },
    true
  );
})();

